import * as React from 'react';
import { FC, useRef } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import { CustomScrollViewProps } from 'components/onboarding-multi-step/types';
import { useEnableNativeTabBarExperiment } from 'experiments/use-enable-native-tab-bar-experiment';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { routes } from 'navigation/routing';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { primitive, spacing } from 'styles/constants';

import { ShortCodeOfferStatusPoller } from '../components/short-code-offer-status-poller';
import { ShortCodePaymentResultPoller } from '../components/short-code-payment-result-poller';
import { TermsText } from '../loyalty-in-restaurant-redemption.styled';
import RewardRedemptionList from '../reward-redemption-list/reward-redemption-list';
import { useLoyaltyMyCode } from '../use-loyalty-my-code';

import { LoyaltyQrCodeLoyaltyIdWidgetRedesign } from './loyalty-qr-code-loyalty-id-widget-redesign';
import { LoyaltyQuickPayWidgetRedesign } from './loyalty-quick-pay-widget-redesign';
import LoyaltyShortCodeWidgetRedesign from './loyalty-short-code-widget-redesign';

const LoyaltyRedemptionRedesign: FC<React.PropsWithChildren> = () => {
  const isMobile = useIsMobileBreakpoint();
  const { isTreatment: isNativeTabBarEnabled } = useEnableNativeTabBarExperiment();

  const { data, loading } = useLoyaltyMyCode();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();

  const scrollViewRef = useRef<CustomScrollViewProps | null>(null);

  if (loading) {
    return null;
  }

  // Offer message
  const addOfferMessage = t('addOffer', 'Add Offer');
  const addOffersMessage = t('addOffers');

  // Reward message
  const addRewardMessage = t('addReward');
  const addRewardsMessage = t('addRewards');

  return (
    <>
      <LayoutContainer isFullContainer={isMobile}>
        <ScrollView
          ref={scrollViewRef}
          contentInsetAdjustmentBehavior={isNativeTabBarEnabled ? 'always' : undefined}
          contentInset={isNativeTabBarEnabled ? { bottom: spacing.$4 } : undefined}
        >
          <Helmet title={t('redeemInRestaurant')} />
          <View style={[styles.contentContainer]}>
            <View style={styles.widgetContainer}>
              <LoyaltyShortCodeWidgetRedesign />
              <LoyaltyQrCodeLoyaltyIdWidgetRedesign />
              <View style={styles.widget}>
                <LoyaltyQuickPayWidgetRedesign />
                <View style={styles.row}>
                  <ActionButton
                    to={routes.loyaltyOfferList}
                    variant={ActionButtonVariants.OUTLINE}
                    style={styles.button}
                  >
                    {enableRewardUnstacking ? addOfferMessage : addOffersMessage}
                  </ActionButton>
                  <ActionButton
                    to={routes.loyaltyRewardsList}
                    variant={ActionButtonVariants.OUTLINE}
                    style={styles.button}
                  >
                    {enableRewardUnstacking && rewardRedemptionLimit === 1
                      ? addRewardMessage
                      : addRewardsMessage}
                  </ActionButton>
                </View>
                <RewardRedemptionList loyaltyEmptyCartTileWidget={null} />
              </View>
            </View>
            {!loading && data?.myCodeLegalTerms?.locale && (
              <View style={styles.termsContainer}>
                <TermsText
                  textProps={{
                    textAlign: 'center',
                    fontSize: '2xs',
                    lineHeight: 'lg',
                    marginLeft: '$10',
                    marginRight: '$10',
                  }}
                  content={data?.myCodeLegalTerms?.locale}
                />
              </View>
            )}
          </View>
        </ScrollView>
      </LayoutContainer>
      <ShortCodeOfferStatusPoller />
      <ShortCodePaymentResultPoller />
    </>
  );
};

const widgetContainerMinHeight = 540;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: spacing.$4,
    marginBottom: spacing.$4,
    marginLeft: spacing.$2,
    marginRight: spacing.$2,
  },
  widgetContainer: {
    minHeight: widgetContainerMinHeight,
  },
  contentContainer: {
    backgroundColor: primitive.$mayoEggWhite,
    paddingTop: spacing.$3,
    paddingBottom: spacing.$3,
    paddingLeft: spacing.$4,
    paddingRight: spacing.$4,
  },
  widget: {
    backgroundColor: 'white',
    marginBottom: spacing.$3,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  termsContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: spacing.$3,
    paddingBottom: spacing.$3,
  },
  button: { borderWidth: 2, flex: 1, marginHorizontal: spacing.$1 },
});

export default LoyaltyRedemptionRedesign;
